import { HIDDEN_FIELD_IGNORE_VALUES, MAX_FIELDS_PER_FORM, MAX_HIDDEN_FIELD_VALUE_LENGTH, MAX_INPUTS_PER_FORM, VALID_INPUT_TYPES } from "@protontech/autofill/constants/heuristics";
import { inputCandidateSelector, kEditorSelector, kFieldSelector, kHiddenUsernameSelector } from "@protontech/autofill/constants/selectors";
import { getNodeRect } from "./dom";
import { flagAsIgnored, flagSubtreeAsIgnored, isClassifiable } from "./flags";

const TABLE_MAX_COLS = 3;
const TABLE_MAX_AREA = 150_000;

/** If 70% of elements are located in a form - we
 * consider it a "page form". This generally means
 * we can sub-cluster */
const PAGE_FORM_RATIO = 0.7;

export const isTopFrame = (): boolean => {
  try {
    return window.self === window.top;
  } catch {
    return false;
  }
};

const nodeOfInterest = (el: HTMLElement) => isClassifiable(el) && el.querySelector("input") !== null;

export const excludeForms = (doc: Document = document) => {
  const bodyElCount = document.body.querySelectorAll("*").length;

  return doc.querySelectorAll("form").forEach((form) => {
    if (nodeOfInterest(form)) {
      /* for performance reasons, we want to avoid running the classifier
       * on forms that have "too many" input fields. These forms are most
       * likely not of interest (data tables, content editors etc..) */
      const fieldCount = form.querySelectorAll(kFieldSelector).length;
      const inputCount = form.querySelectorAll(inputCandidateSelector).length;
      const iframeCount = form.querySelectorAll("iframe").length;
      const invalidFieldCount = inputCount + iframeCount === 0 || inputCount > MAX_INPUTS_PER_FORM || fieldCount > MAX_FIELDS_PER_FORM;

      /* detect top-level forms wrapping the whole page : as a heuristic,
       * compare the number of contained elements to the body's total
       * element count. This allows skipping such forms and relying on
       * the clustering phase to detect "sub-forms" */
      const topFrame = isTopFrame();
      const formElCount = form.querySelectorAll("*").length;
      const pageFormMatch = topFrame && form.matches("body > form");
      const pageFormSignal = topFrame && invalidFieldCount;
      const pageForm = (pageFormMatch || pageFormSignal) && formElCount / bodyElCount >= PAGE_FORM_RATIO;

      const invalidSignal = invalidFieldCount || pageForm;
      if (invalidSignal && !pageForm) return flagSubtreeAsIgnored(form); /* safely ignorable */
      if (invalidSignal && pageForm) return flagAsIgnored(form); /* skip & allow clustering */

      /* if the form is wrapped in a table element only process it if
       * the parent table has max 2 forms : this gets rid of large data
       * tables while preserving edge-case login/register forms located
       * in the same table element. This specific rule works in pair with
       * the `excludedClusterableNodes` pre-pass phase */
      if (form.matches("table form") && form.closest("table")!.querySelectorAll("form").length > 2) return flagAsIgnored(form);
    }
  });
};

export const excludeClusterableNodes = (doc: Document = document) => {
  /* - If a table has a `<thead>` element then it is likely not of interest: we're most likely dealing with a data table.
   * - Do not exclude tables with nested `<table>` elements : let them be excluded individually
   * - If a table has a row whose cellcount outgrows `TABLE_MAX_COLS` then it is unlikely to contain any inputs of interest */
  doc.querySelectorAll("table").forEach((table) => {
    if (nodeOfInterest(table) && !table.querySelector("table") && table.closest("form") === null) {
      const nestedForms = table.querySelectorAll("form");
      if (nestedForms.length > 2) return flagSubtreeAsIgnored(table);

      if (!nestedForms.length) {
        if (table.querySelector("thead") !== null) return flagSubtreeAsIgnored(table);
        const cellCount = Math.max(...Array.from(table.rows).map((row) => row.cells.length));
        if (cellCount > TABLE_MAX_COLS || getNodeRect(table).area > TABLE_MAX_AREA) return flagSubtreeAsIgnored(table);
      }
    }
  });

  /* exclude WYSIWYG-like elements (email composers, document) */
  doc.querySelectorAll<HTMLElement>(kEditorSelector).forEach(flagSubtreeAsIgnored);
};

export const excludeFields = (doc: Document = document) => {
  doc.querySelectorAll("input").forEach((input) => {
    if (!isClassifiable(input)) return;

    /* only keep input with a type we're actually interested with :
     * excludes checkboxes, dates etc.. Keep address autocomplete list
     * fields as we may want to include them for autofilling.. */
    const invalidType = !VALID_INPUT_TYPES.includes(input.type);
    if (invalidType) return flagAsIgnored(input);

    /* only keep hidden input of interest : this especially useful on pages with hundreds
     * of input elements which can cause significant detection slowdowns */
    if (input.type === "hidden") {
      const value = input.value.trim();
      const invalidValueLength = !value.length || value.length > MAX_HIDDEN_FIELD_VALUE_LENGTH;
      if (invalidValueLength || HIDDEN_FIELD_IGNORE_VALUES.includes(value) || !input.matches(kHiddenUsernameSelector)) return flagAsIgnored(input);
    }
  });
};
